import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { PageHeader } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { TableCard, td, trStyle } from "../components/ui/Table";
import { EmptyState, LoadingRow } from "../components/ui/EmptyState";
import { PreviewModal } from "../components/ui/Modal";
import { colors, radius } from "../components/ui/tokens";
import { TrashIcon, EyeIcon } from "../components/ui/icons";

const TYPE_TONE: Record<string, { bg: string; fg: string }> = {
  passion: { bg: "#FFF0F0", fg: "#E24A2B" },
  power: { bg: "#F0F0FF", fg: "#4A3A8B" },
  ritual: { bg: "#F0FFF7", fg: "#1D9E75" },
  community: { bg: "#FFF7E0", fg: "#8a6d1a" },
  feedback: { bg: "#F0F1F3", fg: colors.textMuted },
};

export default function Publications() {
  const [pubs, setPubs] = useState<any[]>([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<any | null>(null);

  const load = () => {
    api.get("/publications").then(data => {
      if (data && Array.isArray(data.publications)) setPubs(data.publications);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = (id: string) => {
    if (!window.confirm("Supprimer cette publication ?")) return;
    api.delete(`/publications/${id}`).then(() => setPubs(prev => prev.filter(p => p.id !== id))).catch(() => {});
  };

  const active = pubs.filter(p => p.status === "active");
  const filtered = active.filter(p => filter === "all" || p.type === filter);
  const filters = ["all", "passion", "power", "ritual", "community", "feedback"];

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Publications" subtitle={`${active.length} publications actives`} />

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {filters.map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "8px 16px", borderRadius: radius.pill,
            border: filter === f ? "none" : `1px solid ${colors.borderStrong}`,
            backgroundColor: filter === f ? colors.text : "white",
            color: filter === f ? "white" : colors.textMuted,
            fontSize: 13, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
          }}>{f === "all" ? "Tout" : f}</button>
        ))}
      </div>

      <TableCard headers={["Publication", "Type", "Auteur", "Prix", "Places", "Statut", "Date", "Actions"]}>
        {loading ? (
          <tr><td colSpan={8}><LoadingRow /></td></tr>
        ) : filtered.length === 0 ? (
          <tr><td colSpan={8}><EmptyState title="Aucune publication" /></td></tr>
        ) : filtered.map(pub => {
          const tone = TYPE_TONE[pub.type] || TYPE_TONE.feedback;
          return (
            <tr key={pub.id} style={trStyle}>
              <td style={{ ...td, fontWeight: 600 }}>{pub.title}</td>
              <td style={td}><span style={{ backgroundColor: tone.bg, color: tone.fg, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: radius.pill }}>{pub.type}</span></td>
              <td style={{ ...td, color: colors.textMuted }}>{pub.author_name}</td>
              <td style={td}>{pub.is_free ? "Gratuit" : `${pub.price}€/pers`}</td>
              <td style={{ ...td, color: colors.textMuted }}>{pub.max_places ? `${pub.current_places}/${pub.max_places}` : "Illimité"}</td>
              <td style={td}><Badge tone="success" dot>{pub.status}</Badge></td>
              <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{new Date(pub.created_at).toLocaleDateString("fr-FR")}</td>
              <td style={td}>
                <div style={{ display: "flex", gap: 6 }}>
                  <Button small variant="secondary" onClick={() => setPreview(pub)}><EyeIcon size={13} /> Consulter</Button>
                  <Button small variant="danger" onClick={() => remove(pub.id)}><TrashIcon size={13} /> Supprimer</Button>
                </div>
              </td>
            </tr>
          );
        })}
      </TableCard>

      <PreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview?.title || ""}
        subtitle={preview ? `${preview.author_name} · ${preview.author_email}` : ""}
        image={preview?.cover_url}
        body={preview?.description}
        fields={preview ? [
          { label: "Type", value: preview.type },
          { label: "Audience", value: preview.audience === "public" ? "Public" : preview.audience === "friends" ? "Amis" : preview.audience },
          { label: "Lieu", value: preview.location_name || "—" },
          { label: "Date de l'événement", value: preview.event_date ? new Date(preview.event_date).toLocaleString("fr-FR") : "—" },
        ] : []}
      />
    </div>
  );
}
